/**
 * GateBonusInfoTooltip Component
 *
 * Info icon with tooltip explaining the Gate wallet campaign bonus
 * Shows:
 * - What the bonus is (2x points for Gate wallet users)
 * - Campaign duration
 * - Progress (X/20,000)
 * - Time remaining
 *
 * Uses Gate's official branding (blue/green gradient).
 * Used on the Points page next to "Your Points" heading
 */

import { useState } from 'react';
import { Info } from 'lucide-react';

import config from '@/config';
import { useGateCampaign } from '@/hooks/useGateCampaign';

// Gate brand colors
const GATE_GREEN = '#17E6A1';
const GATE_BLUE = '#2354E6';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} left`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} left`;
  }
  return 'Less than 1 hour left';
}

export function GateBonusInfoTooltip() {
  const { campaignData, isLoading } = useGateCampaign();
  const [isOpen, setIsOpen] = useState(false);

  // Feature flag check
  if (!config.ENABLE_GATE_WALLET_CAMPAIGN) {
    return null;
  }

  // Only show if campaign is active and user is using Gate wallet
  if (isLoading || !campaignData?.isActive || !campaignData?.isEligible) {
    return null;
  }

  const isEligible = campaignData.isEligible;
  const timeRemaining = formatTimeRemaining(campaignData.endDate);
  const progress = `${campaignData.userBonusEarned.toLocaleString()}/${campaignData.maxBonus.toLocaleString()}`;
  const progressPercent = (campaignData.userBonusEarned / campaignData.maxBonus) * 100;

  return (
    <div className="relative inline-flex">
      {/* Info Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200 hover:scale-110"
        style={{
          background: `linear-gradient(135deg, ${GATE_BLUE}30 0%, ${GATE_GREEN}30 100%)`,
          border: `1px solid ${GATE_GREEN}`,
        }}
      >
        <Info className="w-4 h-4" style={{ color: GATE_GREEN }} />
      </button>

      {/* Tooltip Popover */}
      {isOpen && (
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 p-4 rounded-xl shadow-2xl z-50 animate-slide-in"
          style={{
            backgroundColor: '#343434',
            border: `2px solid transparent`,
            borderImage: `linear-gradient(135deg, ${GATE_BLUE}, ${GATE_GREEN}) 1`,
            boxShadow: `0 0 20px rgba(23, 230, 161, 0.3), 0 0 12px rgba(35, 84, 230, 0.2)`,
          }}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          {/* Header with Icon */}
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg px-2 py-1" style={{ background: 'rgba(240, 240, 240, 0.95)' }}>
              <img src="/gate-color-short.png" alt="Gate" className="h-4" />
            </div>
            <h4
              className="text-base font-bold"
              style={{
                background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontFamily: 'Outfit, sans-serif',
              }}
            >
              2x Bonus Active
            </h4>
          </div>

          {isEligible && campaignData.remainingBonus > 0 ? (
            <>
              {/* Active Bonus Message */}
              <p className="text-sm mb-3" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                You're earning{' '}
                <strong
                  style={{
                    background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  2x points
                </strong>{' '}
                on chest openings with Gate wallet!
              </p>

              {/* Progress Section */}
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-2" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                  <span>Bonus Earned</span>
                  <span style={{ color: GATE_GREEN, fontWeight: 600 }}>{progress} pts</span>
                </div>
                {/* Progress Bar */}
                <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(23, 230, 161, 0.2)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(progressPercent, 100)}%`,
                      background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                    }}
                  />
                </div>
                {campaignData.remainingBonus > 0 && (
                  <p className="text-xs mt-1" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                    {campaignData.remainingBonus.toLocaleString()} pts remaining
                  </p>
                )}
              </div>

              {/* Duration Info */}
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: 'rgba(23, 230, 161, 0.1)',
                  border: `1px solid rgba(23, 230, 161, 0.3)`,
                }}
              >
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: GATE_GREEN, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Cap Reached Message */}
              <p className="text-sm mb-3" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                You've reached the{' '}
                <strong
                  style={{
                    background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  maximum bonus
                </strong>{' '}
                for this campaign!
              </p>

              {/* Completed Badge */}
              <div
                className="p-3 rounded-lg mb-3 text-center"
                style={{
                  background: `linear-gradient(135deg, ${GATE_BLUE} 0%, ${GATE_GREEN} 100%)`,
                }}
              >
                <p className="text-xs font-bold" style={{ color: '#FFFFFF', fontFamily: 'Outfit, sans-serif' }}>
                  20,000 Bonus Points Earned!
                </p>
              </div>

              {/* Duration Info */}
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: 'rgba(23, 230, 161, 0.1)',
                  border: `1px solid rgba(23, 230, 161, 0.3)`,
                }}
              >
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: GATE_GREEN, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
